import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';
import { ReactComponent as ShowPassword } from '../../assets/icons/show-password.svg';
import { ReactComponent as HidePassword } from '../../assets/icons/hide-password.svg';
import { Form, PrimaryButton, FieldTextInput, NamedLink, QuaternaryButton } from '../../components';
import * as validators from '../../util/validators';

import css from './LoginForm.module.css';

const LoginFormComponent = props => (
  <FinalForm
    {...props}
    render={fieldRenderProps => {
      const {
        rootClassName,
        className,
        formId,
        handleSubmit,
        inProgress,
        intl,
        invalid,
        showPassword,
        toggleShow,
        values,
        touched,
        plusLoginButton,
      } = fieldRenderProps;

      // email
      const emailLabel = intl.formatMessage({
        id: 'LoginForm.emailLabel',
      });
      const emailPlaceholder = intl.formatMessage({
        id: 'LoginForm.emailPlaceholder',
      });
      const emailRequiredMessage = intl.formatMessage({
        id: 'LoginForm.emailRequired',
      });
      const emailRequired = validators.required(emailRequiredMessage);
      const emailInvalidMessage = intl.formatMessage({
        id: 'LoginForm.emailInvalid',
      });
      const emailValid = validators.emailFormatValid(emailInvalidMessage);

      // password
      const passwordLabel = intl.formatMessage({
        id: 'LoginForm.passwordLabel',
      });
      const passwordPlaceholder = intl.formatMessage({
        id: 'LoginForm.passwordPlaceholder',
      });
      const passwordRequiredMessage = intl.formatMessage({
        id: 'LoginForm.passwordRequired',
      });
      const passwordRequired = validators.requiredStringNoTrim(passwordRequiredMessage);

      const classes = classNames(rootClassName || css.root, className);
      const submitInProgress = inProgress;
      const submitDisabled = invalid || submitInProgress;

      const passwordRecoveryLink = (
        <NamedLink
          data-testId="password-recovery-link"
          name="PasswordRecoveryPage"
          className={css.recoveryLink}
        >
          <FormattedMessage id="LoginForm.forgotPassword" />
        </NamedLink>
      );
      const passwordValidationError =
        values && touched && !values.password && touched.password === true;

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          <div>
            <FieldTextInput
              data-testid="login-form-email"
              className={css.email}
              type="email"
              id={formId ? `${formId}.email` : 'email'}
              name="email"
              autoComplete="email"
              label={emailLabel}
              placeholder={emailPlaceholder}
              validate={validators.composeValidators(emailRequired, emailValid)}
            />

            <div className={css.inputGroup}>
              {showPassword ? (
                <HidePassword
                  className={passwordValidationError ? css.passwordIconError : css.passwordIcon}
                  onClick={() => toggleShow()}
                />
              ) : (
                <ShowPassword
                  className={passwordValidationError ? css.passwordIconError : css.passwordIcon}
                  onClick={() => toggleShow()}
                />
              )}
              <FieldTextInput
                data-testid="login-form-password"
                className={css.password}
                type={showPassword ? 'text' : 'password'}
                id={formId ? `${formId}.password` : 'password'}
                name="password"
                autoComplete="current-password"
                label={passwordLabel}
                placeholder={passwordPlaceholder}
                validate={passwordRequired}
              />
            </div>
          </div>
          <div className={css.bottomWrapper}>
            <p className={css.bottomWrapperText}>
              <span className={css.recoveryLinkInfo}>{passwordRecoveryLink}</span>
            </p>
            {plusLoginButton ? (
              <QuaternaryButton
                data-testId="login-form-submit"
                type="submit"
                inProgress={submitInProgress}
                disabled={submitDisabled}
                isFullWidth
              >
                <FormattedMessage id="InquirySignupPage.buttonText" />
              </QuaternaryButton>
            ) : (
              <PrimaryButton
                data-testid="login-form-submit"
                type="submit"
                inProgress={submitInProgress}
                disabled={submitDisabled}
                isFullWidth
              >
                <FormattedMessage id="LoginForm.logIn" />
              </PrimaryButton>
            )}
          </div>
        </Form>
      );
    }}
  />
);

LoginFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  form: null,
  inProgress: false,
  plusLoginButton: false,
};

const { string, bool } = PropTypes;

LoginFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  form: string,
  inProgress: bool,
  plusLoginButton: bool,
  intl: intlShape.isRequired,
};

const LoginForm = compose(injectIntl)(LoginFormComponent);
LoginForm.displayName = 'LoginForm';

export default LoginForm;
